import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
import {createInteractionId, mark, types as metrics} from '@servicenow/ui-metrics';
import {produce} from 'immer';
import {
	isDeclarativeUpdate,
	isValidOperation,
	isBatch,
	resolveUpdate,
	isValidOptimisationOperation,
	shouldUpdateState
} from './utils';
import * as operations from '../operators/operations';
import getHostVariables from '../getHostVariables';
import {schedule, types} from '../../scheduler';
import deferHelperExecution from './deferHelperExecution';
import {STATE, RENDER, COMPONENT_ID, CONDITIONAL_DISPATCH, PROPS} from '../../symbols';
import {
	HELPERS_SCHEDULED,
	SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED,
	STATE_UPDATED,
	HELPERS_DEFER,
	COMPONENT_STATE_UPDATED,
	NOW_AVOID_RENDER_PROP,
	SPAN_ATTR_UPDATE_PAYLOAD,
	SPAN_ATTR_DID_UPDATE,
	SPAN_ATTR_SHOULD_RENDER,
	SPAN_FOR_UPDATE_STATE,
	SPAN_KIND_CLIENT
} from '../../constants';
import log from '../../log';
import {emitNowUiEvent, shouldOptOutOfDeepCompareForPage, getSpanOptions} from '../';
import {get, isSelectiveEqual} from '@servicenow/ui-internal';
import {getConfigItem, configConstants} from '@servicenow/ui-config';

const {ENABLE_DECLARATIVE_STATE_UPDATE_DEEP_COMPARE} = configConstants;
let optOutDeepCompare = shouldOptOutOfDeepCompareForPage('state');
const shouldDeepCompareValues = getConfigItem(ENABLE_DECLARATIVE_STATE_UPDATE_DEEP_COMPARE);
const {IMMEDIATE, STATE_SET} = types;
const {
	declarativeOperations: {ASSIGN}
} = operations;
const {trace, isValidTraceId} = uiAnalyticsConsumer;

window.addEventListener(
	'popstate',
	() => (optOutDeepCompare = shouldOptOutOfDeepCompareForPage('state'))
);

function handleStateUpdate(host, hostVariables, update, interactionId) {
	const resolvedUpdate = resolveUpdate(host, hostVariables, update, interactionId);
	const {
		shouldUpdate = true,
		operation = ASSIGN,
		path = '',
		start,
		deleteCount,
		addItems,
		shouldRender = true,
		value
	} = resolvedUpdate;

	if (!shouldUpdate || !shouldUpdateState(hostVariables[STATE], resolvedUpdate))
		return {didUpdate: false, shouldRender: false};

	// this an extremly naive implementation. it doesn't account
	// for operations like push or pop that you'd want to perform
	// before diffing the values
	if (shouldDeepCompareValues && !optOutDeepCompare && path && operation && value) {
		const nextValue = value;
		const currentValue = get(hostVariables[STATE], path);
		if (isSelectiveEqual(currentValue, nextValue)) return {didUpdate: false, shouldRender: false};
	}

	const previousState = hostVariables[STATE];
	const props = hostVariables[PROPS];
	const avoidRender = props && props[NOW_AVOID_RENDER_PROP];
	if (avoidRender && isValidOptimisationOperation(operation)) {
		try {
			const target = isDeclarativeUpdate(resolvedUpdate) ? value : resolvedUpdate;
			const source = path ? get(previousState, path) : previousState;
			if (isSelectiveEqual(source, target)) return {didUpdate: false, shouldRender: false};
		} catch (error) {
			log('Error while performing same state re-render optimisation pre-validation', {
				error,
				host,
				origin: 'handleStateUpdate'
			});
		}
	}

	const nextState = produce(hostVariables[STATE] || {}, draftState => {
		if (operation === 'replace') return value;

		if (!isValidOperation(operation)) {
			log(`Invalid update state operation: ${operation}`, {host, origin: 'handleStateUpdate'});
			return;
		}
		try {
			operations[operation](draftState, {
				path,
				value: isDeclarativeUpdate(resolvedUpdate) ? value : resolvedUpdate,
				start,
				deleteCount,
				addItems: Array.isArray(addItems) ? addItems : []
			});
		} catch (error) {
			log('Error while performing state update', {error, host, origin: 'handleStateUpdate'});
		}
	});

	if (nextState === previousState) return {didUpdate: false, shouldRender: false};

	hostVariables[STATE] = nextState; // eslint-disable-line no-param-reassign
	return {didUpdate: true, shouldRender};
}

function handleStateUpdateBatch(host, hostVariables, batch, interactionId) {
	return batch.reduce(
		(acc, update) => {
			const {shouldRender, didUpdate} = handleStateUpdate(
				host,
				hostVariables,
				update,
				interactionId
			);

			const result = {...acc};
			if (didUpdate) result.didUpdate = true;

			if (shouldRender) result.shouldRender = true;

			return result;
		},
		{didUpdate: false, shouldRender: false}
	);
}

const resolveUpdateForMark = update => (typeof update === 'function' ? 'FUNCTION' : update);
const transformUpdateForMark = (update, isBatch) => {
	if (isBatch) return update.map(resolveUpdateForMark);

	return resolveUpdateForMark(update);
};

export default function createUpdateState(host, executionTiming) {
	const hostVariables = getHostVariables(host);

	return function updateState(update, options = {}) {
		const interactionId = options.interactionId || createInteractionId();
		const {span: activeSpan} = options;
		if (executionTiming === HELPERS_DEFER)
			mark(host, interactionId, metrics.DEFER, {deferred: metrics.UPDATE_STATE});
		const meta = {el: host, interactionId};
		if (activeSpan && isValidTraceId(activeSpan.traceId)) meta.span = activeSpan;
		deferHelperExecution(
			executionTiming,
			() => {
				// TODO: Remove this immediate option for a better scheduling alternative
				// https://code.devsnc.com/gist/dylan-mozlowski/07bf0ab13b2c8a12f0cc6e59bf4cf911
				const {
					[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]:
						optionallyImmediate = false
				} = options;
				const updateStateSpan = trace(SPAN_FOR_UPDATE_STATE, {
					...getSpanOptions(host),
					traceId: interactionId,
					kind: SPAN_KIND_CLIENT,
					parentSpan: meta.span
				});
				schedule({
					fn() {
						const resolvedUpdate = resolveUpdate(host, hostVariables, update, interactionId);
						const shouldBatch = isBatch(resolvedUpdate);

						mark(host, interactionId, metrics.UPDATE_STATE, {
							update: transformUpdateForMark(resolvedUpdate, shouldBatch)
						});

						const previousState = hostVariables[STATE];
						const updateHandler = shouldBatch ? handleStateUpdateBatch : handleStateUpdate;
						const {didUpdate, shouldRender} = updateHandler(
							host,
							hostVariables,
							resolvedUpdate,
							interactionId
						);
						if (!didUpdate) {
							updateStateSpan.setAttribute(SPAN_ATTR_DID_UPDATE, false);
							updateStateSpan.end();
							return;
						}
						updateStateSpan.setAttribute(
							SPAN_ATTR_UPDATE_PAYLOAD,
							transformUpdateForMark(resolvedUpdate, shouldBatch)
						);
						emitNowUiEvent(STATE_UPDATED, {
							componentId: hostVariables[COMPONENT_ID],
							previousState,
							nextState: hostVariables[STATE]
						});

						if (shouldRender) {
							updateStateSpan.setAttribute(SPAN_ATTR_SHOULD_RENDER, true);
							host[RENDER](interactionId, metrics.UPDATE_STATE, {
								update: transformUpdateForMark(resolvedUpdate, shouldBatch),
								activeSpan: updateStateSpan
							});
						} else {
							updateStateSpan.setAttribute(SPAN_ATTR_SHOULD_RENDER, false);
						}
						hostVariables[CONDITIONAL_DISPATCH](
							COMPONENT_STATE_UPDATED,
							{previousState, update},
							{internal: true, span: updateStateSpan}
						);
						updateStateSpan.end();
					},
					meta: {...meta, span: updateStateSpan},
					type: executionTiming !== HELPERS_SCHEDULED || optionallyImmediate ? IMMEDIATE : STATE_SET
				});
			},
			meta
		);
	};
}
